package org.vreo.devicehub.di

import io.ktor.http.URLProtocol
import org.koin.core.module.Module
import org.koin.core.module.dsl.singleOf
import org.koin.core.module.dsl.viewModel
import org.koin.core.module.dsl.viewModelOf
import org.koin.dsl.bind
import org.koin.dsl.module

import org.vreo.devicehub.features.core.data.HttpClientFactory
import org.vreo.devicehub.features.device.data.network.KtorLocalDeviceControl
import org.vreo.devicehub.features.core.data.device.networking.KtorRemoteDeviceControl
import org.vreo.devicehub.features.core.data.device.networking.KtorRemoteDeviceDataSource
import org.vreo.devicehub.features.device.data.network.KtorRemoteMessageDataSource
import org.vreo.devicehub.features.device.domain.DeviceAccessRepository
import org.vreo.devicehub.features.device.data.repository.DeviceAccessRepositoryImpl
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceControl
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceDataSource
import org.vreo.devicehub.features.liveAccess.data.repository.LiveAccessRepositoryDefault
import org.vreo.devicehub.features.liveAccess.domain.LiveAccessRepository
import org.vreo.devicehub.features.liveAccess.domain.WebRtcController
import org.vreo.devicehub.features.device.presentation.SelectedDeviceViewModel
import org.vreo.devicehub.features.device.presentation.addDevice.AddDeviceViewModel
import org.vreo.devicehub.features.device.presentation.deviceList.DeviceListViewModel
import org.vreo.devicehub.features.liveAccess.presentation.LiveAccessViewModel
import org.vreo.devicehub.features.device.presentation.deviceManagement.mainDeviceNavigation.DeviceManagementViewModel
import org.vreo.devicehub.features.device.presentation.deviceManagement.messages.MessageListViewModel
import org.vreo.devicehub.features.vrmOutput.presentation.VrmOutputViewModel
import org.vreo.devicehub.features.device.presentation.registerDevice.RegisterDeviceViewModel
import org.vreo.devicehub.features.roi.data.RoiRepositoryDefault
import org.vreo.devicehub.features.roi.domain.RoiRepository
import org.vreo.devicehub.features.roi.presentation.RoiViewModel
import org.vreo.devicehub.features.settings.data.repository.SettingsRepositoryDefault
import org.vreo.devicehub.features.core.domain.settingsRepository.SettingsRepository
import org.vreo.devicehub.features.deviceDetails.data.repository.DeviceDetailsRepositoryDefault
import org.vreo.devicehub.features.deviceDetails.domain.DeviceDetailsRepository
import org.vreo.devicehub.features.deviceDetails.presentation.DeviceDetailsViewModel
import org.vreo.devicehub.features.settings.presentation.mainSettings.MainSettingsViewModel
import org.vreo.devicehub.features.settings.presentation.settings.SettingsViewModel
import org.vreo.devicehub.features.userAuthentication.data.network.KtorRemoteUserDataSource
import org.vreo.devicehub.features.userAuthentication.data.repository.UserAuthenticationRepositoryImpl
import org.vreo.devicehub.features.userAuthentication.domain.UserAuthenticationRepository
import org.vreo.devicehub.features.userAuthentication.presentation.register.UserRegistrationViewModel
import org.vreo.devicehub.features.userAuthentication.presentation.login.UserLoginViewModel
import org.vreo.devicehub.features.userAuthentication.presentation.verification.AccountVerificationViewModel
import org.vreo.devicehub.features.vrmOutput.data.repository.VrmOutputRepositoryDefault
import org.vreo.devicehub.features.vrmOutput.domain.VrmOutputRepository

// Set to true when debuting using local HTTP (Ktor) server, set to false when using production server
const val DEBUG = false
private val HTTP_PROTOCOL = if (DEBUG) {
    URLProtocol.HTTP
} else {
    URLProtocol.HTTPS
}
private val ENDPOINT = if (DEBUG) {
    "127.0.0.1:9090"
} else {
    "tensor-api-server-c3bkbdc9cjbzfnfz.uksouth-01.azurewebsites.net"
}

// Platform specific components
// Platform specific HTTP module (engine)
expect val platformHttpModule: Module

/**
 * Get platform specific WebRTC controller
 *
 * @return WebRtc controller
 */
expect fun getPlatformWebRtcController(): WebRtcController

/**
 * A global DI (Dependency Injection) module that provides the necessary components for the application.
 *
 * This module defines and configures the dependencies required for HTTP networking, user authentication,
 * data fetching, and view model instantiation. It facilitates the creation, injection, and management
 * of these dependencies across the application.
 *
 * Components included:
 * - **HttpClient**: A configured HTTP client instance for making REST API calls.
 * - **User Authentication Data Sources**: Remote data sources for handling user login, registration,
 *   verification, and related operations.
 * - **User Repositories**: Repositories for managing and coordinating user authentication data flow.
 * - **ViewModels**: View models for handling user login, registration, and account verification business logic.
 *
 * This module ensures that all dependencies are properly initialized and available when needed, adhering to
 * best practices for dependency management and ensuring modular, testable, and scalable code.
 */
val globalAppModules = module {
    // HTTP client
    single { HttpClientFactory.create(get()) }

    //----------------------------------------------------------
    // User authentication
    //----------------------------------------------------------
    single<KtorRemoteUserDataSource> {
        KtorRemoteUserDataSource(
            httpClient = get(),
            hostUrl = ENDPOINT,
            signInRoute = "api/user/authentication/login",
            userDetailsRoute = "api/user/management/details",
            registerRoute = "api/user/authentication/register",
            resendVerificationRoute = "api/user/authentication/resendverification",
            verificationRoute = "api/user/authentication/verify",
            urlProtocol = HTTP_PROTOCOL
        )
    }

    singleOf(::UserAuthenticationRepositoryImpl).bind<UserAuthenticationRepository>()

    viewModelOf(::UserLoginViewModel)
    viewModelOf(::UserRegistrationViewModel)
    viewModel { (userEmail: String, validUntil: Long) ->
        AccountVerificationViewModel(
            userEmail = userEmail,
            validUntilMs = validUntil,
            repository = get(),
        )
    }
    //----------------------------------------------------------
}

/**
 * Dependency Injection module responsible for providing instances and configuration of
 * components related to user sessions and device management. This module includes bindings
 * and ViewModel configurations required for device access, message handling, device settings,
 * live view (WebRTC), ROI management, and VRM output.
 *
 * Overview of key functionalities:
 *
 * - **Device Management**: Provides dependencies for remote and local device control,
 *   device access, and device-related ViewModels.
 * - **Message Handling*/
val userSessionModule = module {
    //----------------------------------------------------------
    // Device
    //----------------------------------------------------------
    single<RemoteDeviceControl> {
        KtorRemoteDeviceControl(
            httpClient = get(),
            hostUrl = ENDPOINT,
            deviceAccessUrl = "api/devices/anpr/command",
            urlProtocol = HTTP_PROTOCOL
        )
    }.bind<RemoteDeviceControl>()

    single<KtorRemoteDeviceDataSource> {
        KtorRemoteDeviceDataSource(
            httpClient = get(),
            hostUrl = ENDPOINT,
            getDevicesRoute = "api/devices/anpr/devices",
            addDeviceToAccountRoute = "api/devices/anpr/link",
            updateDeviceDetailsRoute = "api/devices/anpr/update",
            removeDeviceFromAccountRoute = "api/devices/anpr/unlink",
            registerProductionDeviceRoute = "api/devices/anpr/register",
            deleteDeviceFromProductionRoute = "api/devices/anpr/delete",
            productionDevicesFileRoute = "api/devices/anpr/deviceProductionRegister",
            urlProtocol = HTTP_PROTOCOL
        )
    }.bind<RemoteDeviceDataSource>()

    single<KtorRemoteMessageDataSource> {
        KtorRemoteMessageDataSource(
            httpClient = get(),
            hostUrl = ENDPOINT,
            messagePath = "api/devices/anpr/messages",
            imagesPath = "api/devices/anpr/image",
            urlProtocol = HTTP_PROTOCOL
        )
    }

    single<KtorLocalDeviceControl> {
        KtorLocalDeviceControl(
            httpClient = get(),
            controlUrl = "/api/command",
            urlProtocol = URLProtocol.HTTP
        )
    }

    singleOf(::getPlatformWebRtcController).bind<WebRtcController>()

    singleOf(::DeviceAccessRepositoryImpl).bind<DeviceAccessRepository>()

    viewModelOf(::SelectedDeviceViewModel)

    viewModel { (authenticationToken: String, isAdmin: Boolean) ->
        DeviceListViewModel(
            authenticationToken = authenticationToken,
            repository = get(),
            isAdmin = isAdmin
        )
    }

    viewModel { (authenticationToken: String) ->
        AddDeviceViewModel(
            authenticationToken = authenticationToken,
            repository = get(),
        )
    }

    viewModelOf(::DeviceManagementViewModel)

    viewModel { (authentication: DeviceAuthentication) ->
        MessageListViewModel(
            authentication = authentication,
            repository = get(),
        )
    }

    viewModel { (authenticationToken: String) ->
        RegisterDeviceViewModel(
            authenticationToken = authenticationToken,
            repository = get(),
        )
    }
    //----------------------------------------------------------

    //----------------------------------------------------------
    // Device details
    //----------------------------------------------------------
    singleOf(::DeviceDetailsRepositoryDefault).bind<DeviceDetailsRepository>()

    viewModel { (authentication: DeviceAuthentication, isAdmin: Boolean) ->
        DeviceDetailsViewModel(
            authentication = authentication,
            repository = get(),
            isAdmin = isAdmin
        )
    }
    //----------------------------------------------------------

    //----------------------------------------------------------
    // Live view (WebRTC)
    //----------------------------------------------------------
    singleOf(::LiveAccessRepositoryDefault).bind<LiveAccessRepository>()

    viewModel { (authentication: DeviceAuthentication) ->
        LiveAccessViewModel(
            authentication = authentication,
            repository = get(),
        )
    }
    //----------------------------------------------------------

    //----------------------------------------------------------
    // Device settings
    //----------------------------------------------------------
    singleOf(::SettingsRepositoryDefault).bind<SettingsRepository>()

    viewModel { (authentication: DeviceAuthentication) ->
        MainSettingsViewModel(authentication = authentication)
    }

    viewModel { (authentication: DeviceAuthentication, isAdmin: Boolean) ->
        SettingsViewModel(
            authentication = authentication,
            isAdmin = isAdmin,
            repository = get(),
        )
    }

    //----------------------------------------------------------
    // ROI
    //----------------------------------------------------------
    singleOf(::RoiRepositoryDefault).bind<RoiRepository>()

    viewModel { (authentication: DeviceAuthentication) ->
        RoiViewModel(
            authentication = authentication,
            repository = get()
        )
    }
    //----------------------------------------------------------

    //----------------------------------------------------------
    // VRM output
    //----------------------------------------------------------
    singleOf(::VrmOutputRepositoryDefault).bind<VrmOutputRepository>()

    viewModel { (authentication: DeviceAuthentication) ->
        VrmOutputViewModel(
            authentication = authentication,
            vrmRepository = get(),
            settingsRepository = get()
        )
    }
    //----------------------------------------------------------
}

